import React, { useState } from "react";
import { POS_COLORS } from "../../data/players";
import { boardTotals } from "../../utils/liveBoard";
import theme from "../../data/theme";

/**
 * DraftLog — every pick, in the order it was entered.
 *
 * ══════════════════════════════════════════════════════════════════════
 * 🔴 THIS IS A VIEW, NOT STATE. It renders `board.sales` and nothing else.
 * NOTHING is added to sanitizeLiveBoard for it, and draftLog.test.js pins that
 * whitelist's key set so a later change cannot quietly slip one in.
 *
 * That whitelist has silently eaten a key five times, and the failure mode is
 * always the same: the board looks right all session and something is gone
 * after a refresh. A feature that needs none of that risk should take none of
 * it. If you find yourself adding a persisted array to build something here,
 * STOP — the requirement has changed and it needs a different conversation.
 * ══════════════════════════════════════════════════════════════════════
 *
 * 🔴 WHO NOMINATED EACH PLAYER IS NOT HERE, AND THAT IS DELIBERATE.
 * `nominatorIndex` is documented as ADVISORY everywhere it appears — "a
 * prediction the user can correct, never a gate" (liveBoard.js). Real auctions
 * skip full teams and drift, which is the entire reason `skip ahead →` exists.
 *
 * A running log is the one place a prediction hardens into a record. "PJ
 * nominated Bijan" sitting in a chronological list reads as fact, gets
 * screenshotted, and gets argued about — and it is a guess nobody corrected
 * because until now nothing depended on it. The board is honest about that
 * number everywhere else; a permanent log is exactly where that honesty would
 * break. Same reasoning for cancelled nominations: a nomination that never sold
 * left no trace, and as far as the auction's record goes, nothing happened.
 *
 * So this lists PICKS. Every pick was a nomination, so in practice it is the
 * list that was asked for.
 *
 * 🔴 NO posRank, AND NO ESTIMATE. `posRank` is on the sale record and is left
 * off on purpose — the log is a record of what happened, not a valuation, which
 * is the same line NominationBlock already holds. The header does count and
 * total spent and stops there: that is arithmetic anyone at the table could do
 * in their head, which is the standing test. No pace, no projections, no "you
 * are spending faster than the room."
 *
 * NEWEST FIRST BY DEFAULT, against the literal ask for chronological. At pick
 * 90 of 168, chronological means scrolling past 89 rows to reach the thing you
 * opened the tab to check, and the question this answers nine times out of ten
 * is "what did that just go for." Chronological is one click away.
 *
 * EDITABLE ROWS. `onEditSale` takes the sale's index in `board.sales` and opens
 * the same SaleEditor the roster cards do — one handler, one editor. Finding a
 * mistyped price by WHEN it happened beats hunting a 10-card grid for the
 * player, and a wrong price entered ninety minutes ago is a real draft-night
 * event. Omit the prop for a read-only log; /watch passes nothing, exactly as
 * it does for the roster grid.
 */
const GRID = "42px minmax(0,1fr) 46px 56px minmax(0,1.05fr)";

export default function DraftLog({ board, onEditSale = null }) {
  const [newestFirst, setNewestFirst] = useState(true);

  const sales = board?.sales || [];
  const teamNames = board?.teamNames || [];
  // 🔴 READ, NOT RE-SUMMED. boardTotals owns this number and its comment
  // declares it the canonical fact — "what the room has actually paid, in
  // total" — as distinct from roomTemp's tally, which is an opinion. A second
  // sum here would be a second definition, and the day they disagree is the day
  // somebody at the table is reading two totals off one app.
  const spent = boardTotals(board).spent;
  const editable = typeof onEditSale === "function";

  // 🔴 THE PICK NUMBER IS THE COMMIT INDEX, NEVER THE ROW'S POSITION ON SCREEN.
  // Flipping the sort must not renumber the auction — pick 12 is pick 12 read
  // from either end, and it is also the index the editor needs.
  const rows = sales.map((sale, index) => ({ sale, index }));
  if (newestFirst) rows.reverse();

  const orderBtn = (label, active, onClick) => (
    <button
      onClick={onClick}
      style={{
        background: active ? theme.colors.brand.orange : "transparent",
        border: `1px solid ${active ? theme.colors.brand.orange : theme.colors.border.default}`,
        borderRadius: theme.radius.sm, color: active ? "#fff" : theme.colors.text.muted,
        fontFamily: "inherit", fontSize: theme.fontSize.micro, fontWeight: theme.fontWeight.bold,
        padding: "3px 9px", cursor: "pointer", letterSpacing: "0.04em",
      }}
    >{label}</button>
  );

  const headCell = {
    fontSize: theme.fontSize.micro, textTransform: "uppercase", letterSpacing: "0.08em",
    color: theme.colors.text.faint, fontWeight: theme.fontWeight.bold,
  };

  return (
    <div data-testid="draft-log">
      <div style={{
        display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 10,
      }}>
        {/* Count and total spent. Both are facts already printed on the roster
            cards; this is the sum, not a read on the market. */}
        <span style={{
          fontSize: theme.fontSize.body, fontWeight: theme.fontWeight.bold,
          color: theme.colors.text.secondary, fontVariantNumeric: "tabular-nums",
        }}>
          {sales.length} {sales.length === 1 ? "pick" : "picks"} · ${spent} spent
        </span>
        <span style={{ marginLeft: "auto", ...headCell }}>Order</span>
        {orderBtn("Newest first", newestFirst, () => setNewestFirst(true))}
        {orderBtn("Chronological", !newestFirst, () => setNewestFirst(false))}
      </div>

      {sales.length === 0 ? (
        <div style={{
          color: theme.colors.text.faint, fontSize: theme.fontSize.body,
          padding: "26px 4px", textAlign: "center",
        }}>
          Nothing entered yet. Every pick shows up here as it is committed.
        </div>
      ) : (
        <>
          <div style={{
            display: "grid", gridTemplateColumns: GRID, gap: 8, alignItems: "center",
            padding: "0 10px 6px", borderBottom: `1px solid ${theme.colors.border.subtle}`,
          }}>
            <span style={headCell}>#</span>
            <span style={headCell}>Player</span>
            <span style={headCell}>Pos</span>
            <span style={{ ...headCell, textAlign: "right" }}>Price</span>
            <span style={headCell}>Team</span>
          </div>

          <div style={{ display: "flex", flexDirection: "column" }}>
            {rows.map(({ sale, index }) => {
              const pos = POS_COLORS[sale.pos];
              const team = teamNames[sale.team] ?? "";
              const rowStyle = {
                display: "grid", gridTemplateColumns: GRID, gap: 8, alignItems: "center",
                width: "100%", padding: "6px 10px", textAlign: "left",
                background: index % 2 === 0 ? theme.colors.bg.surface : "transparent",
                border: "none", borderRadius: theme.radius.sm,
                fontFamily: "inherit", fontSize: theme.fontSize.caption,
                cursor: editable ? "pointer" : "default",
              };
              const cells = (
                <>
                  <span style={{
                    color: theme.colors.text.faint, fontVariantNumeric: "tabular-nums",
                  }}>{index + 1}</span>
                  <span style={{
                    color: theme.colors.text.primary, fontWeight: theme.fontWeight.bold,
                    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                  }}>{sale.name}</span>
                  <span className="tag" style={{
                    background: pos?.bg || theme.colors.bg.elevated,
                    color: pos?.text || theme.colors.text.muted,
                    fontSize: theme.fontSize.micro, padding: "1px 5px",
                    textAlign: "center", fontWeight: theme.fontWeight.bold,
                  }}>{sale.pos}</span>
                  <span style={{
                    color: theme.colors.brand.accent, fontWeight: theme.fontWeight.bold,
                    textAlign: "right", fontVariantNumeric: "tabular-nums",
                  }}>${sale.price}</span>
                  <span style={{
                    color: theme.colors.text.secondary,
                    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                  }}>{team}</span>
                </>
              );

              // A real <button> when it is editable, so the whole row is
              // keyboard-reachable without inventing a role and a key handler.
              return editable ? (
                <button
                  key={index}
                  type="button"
                  data-testid={`log-row-${index}`}
                  onClick={() => onEditSale(index)}
                  aria-label={`Pick ${index + 1}: ${sale.name}, $${sale.price} to ${team}. Click to correct or delete`}
                  style={rowStyle}
                >{cells}</button>
              ) : (
                <div key={index} data-testid={`log-row-${index}`} style={rowStyle}>{cells}</div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
